/**
 * The HUD's hint card (AD-844; docs/design/guest-demo.md §5.7): one short, input-aware line at the
 * bottom centre, above the context prompt and the action row — clear of the toast column (top
 * right, AD-839) and of the play area's centre. It takes taps only on itself (its "Got it" closes it — not a ×, which the panels' close owns), so
 * a tap on the world beside it still reaches the world. The rules live in `hintEngine.ts`, the
 * rows in `hintCatalog.ts`; this file only builds the context, renders, and reports the counters.
 */

import { useEffect, useRef, useState } from 'react';

import type { GameStatus } from '../../game/Game.js';
import { useGame } from '../net-context.js';
import { DEMO_BOSS_NAME_MATCH, DEMO_HINTS } from './hintCatalog.js';
import {
  dismissHint,
  initialHintState,
  loadSeen,
  saveSeen,
  stepHints,
  type HintContext,
  type HintInput,
  type HintState,
} from './hintEngine.js';

/** The avatar counts as having walked once it is this far from where it arrived (m). */
const MOVED_M = 4;
/** A hostile within this range triggers the fight hint (m; the enemy aggro radius). */
const HOSTILE_NEAR_M = 12;
/** The boss within this range triggers the decoy hint (m). */
const BOSS_NEAR_M = 20;

function storage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

/** How the player is playing right now: a coarse pointer reads as touch. */
export function inputMode(): HintInput {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
    ? 'touch'
    : 'desktop';
}

export function HintCard({ status }: { status: GameStatus }): JSX.Element | null {
  const { state, conn } = useGame();
  const rt = useRef<HintState>(initialHintState(loadSeen(storage())));
  const arrivedAt = useRef<{ x: number; z: number } | null>(null);
  const moved = useRef(false);
  const [, rerender] = useState(0);
  // The engine retires a hint on time too; a slow clock re-runs it while one is up.
  useEffect(() => {
    const t = setInterval(() => rerender((n) => n + 1), 500);
    return () => clearInterval(t);
  }, []);

  const self = state.selfEntityId != null ? state.entities.get(state.selfEntityId) : undefined;
  if (self?.x != null && self.z != null) {
    if (arrivedAt.current === null) arrivedAt.current = { x: self.x, z: self.z };
    else if (Math.hypot(self.x - arrivedAt.current.x, self.z - arrivedAt.current.z) > MOVED_M)
      moved.current = true;
  }
  let hostileNear = false;
  let bossNear = false;
  if (self?.x != null && self.z != null) {
    for (const e of state.entities.values()) {
      if (e.kind !== 'enemy' || e.x == null || e.z == null || (e.health ?? 1) <= 0) continue;
      const d = Math.hypot(e.x - self.x, e.z - self.z);
      if (d <= HOSTILE_NEAR_M) hostileNear = true;
      if (d <= BOSS_NEAR_M && e.name?.includes(DEMO_BOSS_NAME_MATCH)) bossNear = true;
    }
  }
  const bar = [
    ...(state.loadout?.weaponAbilities ?? []),
    ...(state.loadout?.chosenAbilities ?? []),
  ];
  const ctx: HintContext = {
    input: inputMode(),
    quests: state.quests?.quests ?? [],
    moved: moved.current,
    hostileNear,
    bossNear,
    hpFrac: status.healthMax > 0 ? status.health / status.healthMax : 1,
    dead: status.dead,
    struck: state.combatLog.length > 0,
    slotOf: (id) => {
      const i = bar.indexOf(id);
      return i < 0 ? null : i + 1;
    },
  };

  useEffect(() => {
    const { state: next, events } = stepHints(DEMO_HINTS, rt.current, ctx, Date.now());
    if (next === rt.current) return;
    if (next.seen !== rt.current.seen) saveSeen(storage(), next.seen);
    rt.current = next;
    for (const e of events) conn.send('hintEvent', e);
    rerender((n) => n + 1);
  });

  const active = rt.current.active;
  const def = active ? DEMO_HINTS.find((d) => d.id === active.id) : undefined;
  if (!def) return null;
  return (
    <div
      className="hud-hint interactive"
      role="status"
      data-testid="hud-hint"
      data-hint={def.id}
      data-hud-notice
    >
      <span className="hud-hint-text">{def.text(ctx)}</span>
      <button
        type="button"
        className="hud-hint-close"
        aria-label="dismiss hint"
        data-testid="hud-hint-dismiss"
        onClick={() => {
          rt.current = dismissHint(rt.current, Date.now());
          rerender((n) => n + 1);
        }}
      >
        Got it
      </button>
    </div>
  );
}
